/**
 * Curated profession list with Lucide icons.
 *
 * Replaces the free-form `profession` text input in the wizard with
 * a select. Free-form text remains schema-legal (column type is
 * text, not enum) so users with profiles already filled in stay
 * valid; we hit them with a re-pick prompt on next edit if their
 * current value is off-list.
 *
 * Icons: Lucide kebab-case names. Resolve via TraitIcon.
 */

import type { TraitOption } from '../types.js';

export const PROFESSION_OPTIONS: readonly TraitOption[] = [
  // ─── Tech & data ───────────────────────────────────────────────
  { value: 'software_engineer', labelKey: 'profession.software_engineer', icon: 'code' },
  { value: 'data_scientist', labelKey: 'profession.data_scientist', icon: 'chart-line' },
  { value: 'product_manager', labelKey: 'profession.product_manager', icon: 'rocket' },
  { value: 'designer', labelKey: 'profession.designer', icon: 'palette' },
  { value: 'systems_administrator', labelKey: 'profession.systems_administrator', icon: 'server' },
  { value: 'cybersecurity', labelKey: 'profession.cybersecurity', icon: 'shield' },

  // ─── Healthcare ────────────────────────────────────────────────
  { value: 'doctor', labelKey: 'profession.doctor', icon: 'stethoscope' },
  { value: 'dentist', labelKey: 'profession.dentist', icon: 'tooth' },
  { value: 'pharmacist', labelKey: 'profession.pharmacist', icon: 'pill' },
  { value: 'nurse', labelKey: 'profession.nurse', icon: 'heart-pulse' },
  { value: 'therapist', labelKey: 'profession.therapist', icon: 'brain' },
  { value: 'veterinarian', labelKey: 'profession.veterinarian', icon: 'paw-print' },

  // ─── Engineering & built environment ───────────────────────────
  { value: 'civil_engineer', labelKey: 'profession.civil_engineer', icon: 'building-2' },
  { value: 'mechanical_engineer', labelKey: 'profession.mechanical_engineer', icon: 'cog' },
  { value: 'electrical_engineer', labelKey: 'profession.electrical_engineer', icon: 'zap' },
  { value: 'architect', labelKey: 'profession.architect', icon: 'compass' },

  // ─── Business / finance ────────────────────────────────────────
  { value: 'accountant', labelKey: 'profession.accountant', icon: 'calculator' },
  { value: 'auditor', labelKey: 'profession.auditor', icon: 'file-search' },
  { value: 'financial_analyst', labelKey: 'profession.financial_analyst', icon: 'trending-up' },
  { value: 'investment_banker', labelKey: 'profession.investment_banker', icon: 'banknote' },
  { value: 'consultant', labelKey: 'profession.consultant', icon: 'briefcase' },
  { value: 'entrepreneur', labelKey: 'profession.entrepreneur', icon: 'rocket' },
  { value: 'business_owner', labelKey: 'profession.business_owner', icon: 'store' },
  { value: 'sales', labelKey: 'profession.sales', icon: 'handshake' },
  { value: 'marketing', labelKey: 'profession.marketing', icon: 'megaphone' },
  { value: 'hr', labelKey: 'profession.hr', icon: 'users' },
  { value: 'operations', labelKey: 'profession.operations', icon: 'list-checks' },
  { value: 'real_estate', labelKey: 'profession.real_estate', icon: 'home' },

  // ─── Law & government ─────────────────────────────────────────
  { value: 'lawyer', labelKey: 'profession.lawyer', icon: 'scale' },
  { value: 'judge', labelKey: 'profession.judge', icon: 'gavel' },
  { value: 'civil_servant', labelKey: 'profession.civil_servant', icon: 'landmark' },
  { value: 'diplomat', labelKey: 'profession.diplomat', icon: 'globe' },

  // ─── Education & faith ────────────────────────────────────────
  { value: 'teacher', labelKey: 'profession.teacher', icon: 'graduation-cap' },
  { value: 'professor', labelKey: 'profession.professor', icon: 'library' },
  { value: 'researcher', labelKey: 'profession.researcher', icon: 'microscope' },
  { value: 'imam', labelKey: 'profession.imam', icon: 'moon-star' },
  { value: 'islamic_scholar', labelKey: 'profession.islamic_scholar', icon: 'book-open' },
  { value: 'student', labelKey: 'profession.student', icon: 'graduation-cap' },

  // ─── Creative ──────────────────────────────────────────────────
  { value: 'writer', labelKey: 'profession.writer', icon: 'pen-tool' },
  { value: 'journalist', labelKey: 'profession.journalist', icon: 'newspaper' },
  { value: 'photographer', labelKey: 'profession.photographer', icon: 'camera' },
  { value: 'filmmaker', labelKey: 'profession.filmmaker', icon: 'film' },
  { value: 'musician', labelKey: 'profession.musician', icon: 'music' },
  { value: 'artist', labelKey: 'profession.artist', icon: 'palette' },

  // ─── Trades & services ────────────────────────────────────────
  { value: 'chef', labelKey: 'profession.chef', icon: 'chef-hat' },
  { value: 'restaurateur', labelKey: 'profession.restaurateur', icon: 'utensils' },
  { value: 'electrician', labelKey: 'profession.electrician', icon: 'plug' },
  { value: 'plumber', labelKey: 'profession.plumber', icon: 'wrench' },
  { value: 'driver', labelKey: 'profession.driver', icon: 'car' },
  { value: 'pilot', labelKey: 'profession.pilot', icon: 'plane' },

  // ─── Public safety & military ─────────────────────────────────
  { value: 'firefighter', labelKey: 'profession.firefighter', icon: 'flame' },
  { value: 'police', labelKey: 'profession.police', icon: 'shield' },
  { value: 'military', labelKey: 'profession.military', icon: 'shield-check' },

  // ─── Other ────────────────────────────────────────────────────
  { value: 'homemaker', labelKey: 'profession.homemaker', icon: 'home' },
  { value: 'retired', labelKey: 'profession.retired', icon: 'umbrella' },
  { value: 'between_jobs', labelKey: 'profession.between_jobs', icon: 'compass' },
  { value: 'self_employed', labelKey: 'profession.self_employed', icon: 'briefcase' },
  { value: 'other', labelKey: 'profession.other', icon: 'more-horizontal' },
  { value: 'prefer_not_say', labelKey: 'profession.prefer_not_say', icon: 'lock' },
] as const;

export const PROFESSION_VALUES: ReadonlySet<string> = new Set(
  PROFESSION_OPTIONS.map((o) => o.value),
);
